import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SavedDrawer from "./SavedDrawer";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Listings", id: "listings" },
  { label: "Open Houses", id: "open-houses", to: "/open-houses" },
  { label: "Services", id: "services" },
  { label: "Calculator", id: "calculator" },
  { label: "Testimonials", id: "testimonials" },
  { label: "Contact", id: "contact" },
];

export default function Nav({ name = "Mabel McMillan", logo = "" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToWhenReady = (id) => {
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (!el) { if (++tries > 30) clearInterval(timer); return; }
      const r = el.getBoundingClientRect();
      if (r.top >= -80 && r.top < window.innerHeight * 0.5) { clearInterval(timer); return; }
      el.scrollIntoView({ behavior: "auto", block: "start" });
      if (++tries > 30) clearInterval(timer);
    }, 300);
  };

  const goto = (l) => {
    setOpen(false);
    if (l.to) { navigate(l.to); return; }
    if (location.pathname !== "/") {
      navigate("/");
      scrollToWhenReady(l.id);
    } else {
      document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-[#0A1128]/10 py-4" : "py-6 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          {logo ? (
            <img src={logo} alt={name} className="h-11 md:h-12 w-auto object-contain" />
          ) : (
            <>
              <span className="grid place-items-center w-9 h-9 border border-[#0A1128] text-[#0A1128] font-serif text-lg leading-none group-hover:bg-[#0A1128] group-hover:text-[#FAF9F6] transition-colors duration-300">
                {initials}
              </span>
              <span className="font-serif text-xl tracking-tight text-[#0A1128] hidden sm:block">{name}</span>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => goto(l)}
              className="text-[13px] uppercase tracking-[0.14em] text-[#0A1128] link-underline"
            >
              {l.label}
            </button>
          ))}
          <SavedDrawer />
          <button
            data-testid="nav-inquire"
            onClick={() => goto({ id: "contact" })}
            className="text-[13px] uppercase tracking-[0.14em] px-6 py-3 bg-[#0A1128] text-[#FAF9F6] hover:bg-[#1a2342] transition-colors duration-300"
          >
            Inquire
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden flex flex-col gap-[5px] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          <span className={`w-6 h-px bg-[#0A1128] transition-transform ${open ? "rotate-45 translate-y-[6px]" : ""}`} />
          <span className={`w-6 h-px bg-[#0A1128] transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`w-6 h-px bg-[#0A1128] transition-transform ${open ? "-rotate-45 -translate-y-[6px]" : ""}`} />
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#FAF9F6] border-t border-[#0A1128]/10 mt-4 px-5 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <button key={l.id} data-testid={`nav-mobile-${l.id}`} onClick={() => goto(l)}
              className="text-left text-sm uppercase tracking-[0.14em] text-[#0A1128]">
              {l.label}
            </button>
          ))}
          <div className="pt-2"><SavedDrawer /></div>
        </div>
      )}
    </header>
  );
}
